import { PortfolioSelection, PortfolioValue } from '@components/index';
import { Home } from '@mui/icons-material';
import { Box, Breadcrumbs, Container, Divider, Typography } from '@mui/material';
import { ScreenState } from '@src/app/enums';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { setAsOfDate } from '@src/app/store/reducers/holdings';
import { useHoldingsPolling } from '@src/app/hooks/useHoldingsPolling';
import { FC, useEffect, useState } from 'react';
import { HoldingsDataGrid } from './HoldingsDataGrid';
import { useWindow } from '@src/app/hooks/useWindow';
import { selectHoldings } from '@src/app/store/reducers/holdings/selectors';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { useAppContext } from '@src/app/hooks/useAppContext';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';
import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { PageContent } from '@components/pageContent';
import { useHistory } from 'react-router';
import { homeUrl } from '@src/app/utils/UrlBuilder';
import { Grid } from '@mui/material';

const Holdings: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { resizing } = useWindow();
  const { transitioningMenu } = useAppContext();

  const [currentPageSize, setCurrentPageSize] = useState<number>(25);
  const { fetchHoldings } = useHoldingsPolling();

  const { portfoliosSelected, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { data, state, asOfDate, filters } = useAppSelector(selectHoldings);

  const setAsAtDate = (date: DateRange) => {
    dispatch(setAsOfDate(date));
  };

  const fetchHoldingsAsync = async () => {
    if (portfolioInfoState !== ScreenState.SuccessDataLoaded) {
      return;
    }

    await fetchHoldings({
      asOfDate: asOfDate.dateTo,
      clientPortfolios: portfoliosSelected,
    });
  };

  useEffect(() => {
    fetchHoldingsAsync();
  }, [asOfDate, portfoliosSelected, portfolioInfoState]);

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" gap="16px" mt={2} sx={{ alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
          <Breadcrumbs>
            <Typography
              onClick={() => history.push(homeUrl())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
              color="inherit"
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              Dashboard
            </Typography>
            <Typography fontWeight={600} fontSize="20px" color="#222222">
              Holdings
            </Typography>
          </Breadcrumbs>

          <Box flex={1} />

          <Box display="flex" gap="16px">
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker isDateOnly dateRange={asOfDate} setDateRange={(dateRange: DateRange) => setAsAtDate(dateRange)} />
            </LocalizationProvider>
            <PortfolioSelection allowCombining={false} disabled={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading} />
          </Box>
        </Box>
        <PageContent isLoading={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading}>
          <Grid container spacing={8}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <PortfolioValue totalValue={data?.totalValue ?? 0} />
            </Grid>
          </Grid>

          <Divider />

          {!resizing && !transitioningMenu && (
            <HoldingsDataGrid
              filters={filters || []}
              asOfDate={asOfDate.dateTo}
              data={data}
              currentPageSize={currentPageSize}
              setCurrentPageSize={setCurrentPageSize}
            />
          )}
        </PageContent>
      </Box>
    </Container>
  );
};

export default Holdings;
